<script setup>
/* eslint-disable vue/custom-event-name-casing */
import { ref, computed, watch, onMounted, nextTick, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import InlineEditField from 'dashboard/components-next/pipeline/inline/InlineEditField.vue';
import PipelineCardLinksAPI from 'dashboard/api/pipelineCardLinks';
import { useAlert } from 'dashboard/composables';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import {
  formatInReportingTimezone,
  resolveReportingTimezone,
  zonedDateTimeToUtc,
} from '../../utils/localeFormatting';
import { getEffectiveCardValue } from '../../helpers/columnFilterMatchers';

// ── Props ─────────────────────────────────────────────────────────────────────

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  customAttributes: {
    type: Array,
    default: () => [],
  },
  contactCustomAttributes: {
    type: Array,
    default: () => [],
  },
  notesCount: {
    type: Number,
    default: 0,
  },
  checklistTotal: {
    type: Number,
    default: 0,
  },
  checklistCompleted: {
    type: Number,
    default: 0,
  },
  attachmentsCount: {
    type: Number,
    default: 0,
  },
  stageChangesCount: {
    type: Number,
    default: 0,
  },
});

// ── Emits ─────────────────────────────────────────────────────────────────────

const emit = defineEmits([
  'goToTab',
  'updateField',
  'unlinkConversation',
  'linkConversation',
  'overrideLeadScore',
  'itemUpdated',
]);

// ── Composables ───────────────────────────────────────────────────────────────

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();
const { showAlert } = useAlert();
const panelBaseId = `pipeline-card-info-${useId()}`;
const panelContentId = panel => `${panelBaseId}-${panel}`;
const contactPickerId = `${panelBaseId}-contact-picker`;
const contactSearchId = `${panelBaseId}-contact-search`;
const conversationInputId = `${panelBaseId}-conversation-input`;
const conversationFieldId = `${panelBaseId}-conversation-id`;
const metadataSeparator = '—';
const normalizePositiveId = value => {
  const normalizedValue = String(value ?? '').trim();
  return /^[1-9]\d*$/.test(normalizedValue) ? normalizedValue : '';
};
const actionConfirmationRef = ref(null);
const confirmationTitle = ref('');
const confirmationDescription = ref('');
const confirmationLabel = ref('');
const isConfirmingAction = ref(false);

const requestConfirmation = async ({ title, description, label }) => {
  if (isConfirmingAction.value) return false;
  isConfirmingAction.value = true;
  confirmationTitle.value = title;
  confirmationDescription.value = description;
  confirmationLabel.value = label;
  await nextTick();
  try {
    return await actionConfirmationRef.value?.showConfirmation();
  } finally {
    isConfirmingAction.value = false;
  }
};

// ── localStorage collapse state ───────────────────────────────────────────────

const STORAGE_KEY_PREFIX = 'pipelineCard.panels.';

const currentUserId = computed(
  () => store.getters.getCurrentUser?.id || 'default'
);

const storageKey = computed(
  () => `${STORAGE_KEY_PREFIX}${currentUserId.value}`
);

const loadPanelState = () => {
  try {
    const raw = localStorage.getItem(storageKey.value);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore parse errors
  }
  // defaults: all open except additionalData and customFields
  return {
    contact: true,
    statistics: true,
    itemInfo: true,
    linkedConversation: true,
    additionalData: false,
    customFields: false,
  };
};

const panelOpen = ref(loadPanelState());

const savePanelState = () => {
  try {
    localStorage.setItem(storageKey.value, JSON.stringify(panelOpen.value));
  } catch {
    // ignore storage errors
  }
};

watch(panelOpen, savePanelState, { deep: true });

// Re-load when user changes (e.g. account switch)
watch(storageKey, () => {
  panelOpen.value = loadPanelState();
});

const togglePanel = key => {
  panelOpen.value = { ...panelOpen.value, [key]: !panelOpen.value[key] };
};

// ── Contact info ──────────────────────────────────────────────────────────────

const contactAvatarUrl = computed(() => {
  const d = props.item?.item_details;
  if (d?.contact_thumbnail?.trim()) return d.contact_thumbnail;
  const conv = d?.conversation?.contact;
  if (conv?.thumbnail?.trim()) return conv.thumbnail;
  if (conv?.avatar_url?.trim()) return conv.avatar_url;
  if (props.item?.contact?.thumbnail?.trim())
    return props.item.contact.thumbnail;
  return '';
});

const contactName = computed(
  () => props.item?.contact?.name || props.item?.item_details?.company || ''
);

const contactPhone = computed(
  () =>
    props.item?.contact?.phone_number || props.item?.item_details?.phone || ''
);

const contactEmail = computed(
  () => props.item?.contact?.email || props.item?.item_details?.email || ''
);

const contactId = computed(
  () =>
    props.item?.contact?.id ||
    props.item?.item_details?.conversation?.contact?.id ||
    null
);

const navigateToContact = () => {
  if (!contactId.value) return;
  const accountId = store.getters.getCurrentAccountId;
  try {
    router.push({
      name: 'contacts_show',
      params: { accountId, id: contactId.value },
    });
  } catch {
    window.location.href = `/app/accounts/${accountId}/contacts/${contactId.value}`;
  }
};

// ── FR2: Additional contacts (multiple contacts per card) ─────────────────────
// Backend: PipelineCard#as_json['additional_contacts'] — the primary contact
// (contact_id) is never duplicated in this array (deduped server-side).

const additionalContacts = computed(
  () => props.item?.additional_contacts || []
);

const linkedContactIds = computed(() => {
  const ids = new Set(additionalContacts.value.map(c => c.contact_id));
  if (contactId.value) ids.add(contactId.value);
  return ids;
});

const showContactPicker = ref(false);
const contactQuery = ref('');
const contactSearchResults = ref([]);
const contactSearchLoading = ref(false);
const addingContact = ref(false);
const removingContactId = ref(null);
let contactSearchTimer = null;

const toggleContactPicker = () => {
  showContactPicker.value = !showContactPicker.value;
  if (!showContactPicker.value) {
    contactQuery.value = '';
    contactSearchResults.value = [];
  }
};

const searchContactsForAdd = () => {
  const query = contactQuery.value.trim();
  if (query.length < 2) {
    contactSearchResults.value = [];
    return;
  }
  contactSearchLoading.value = true;
  clearTimeout(contactSearchTimer);
  contactSearchTimer = setTimeout(async () => {
    try {
      await store.dispatch('contacts/search', { search: query, page: 1 });
      contactSearchResults.value = (store.getters['contacts/getContacts'] || [])
        .filter(c => !linkedContactIds.value.has(c.id))
        .slice(0, 8);
    } catch {
      contactSearchResults.value = [];
    } finally {
      contactSearchLoading.value = false;
    }
  }, 300);
};

const handleAddContact = async contact => {
  addingContact.value = true;
  try {
    await PipelineCardLinksAPI.addContact(props.item.id, contact.id);
    showAlert(t('PIPELINE.PANELS.LINKS.ADD_CONTACT_SUCCESS'));
    showContactPicker.value = false;
    contactQuery.value = '';
    contactSearchResults.value = [];
    emit('itemUpdated');
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      error?.response?.data?.error ||
      t('PIPELINE.PANELS.LINKS.ADD_CONTACT_ERROR');
    showAlert(message);
  } finally {
    addingContact.value = false;
  }
};

const linkRemovalContext = (type, link) => ({
  accountId: normalizePositiveId(store.getters.getCurrentAccountId),
  cardId: normalizePositiveId(props.item?.id),
  contactId: normalizePositiveId(contactId.value),
  linkId: normalizePositiveId(link?.id),
  targetId: normalizePositiveId(
    type === 'contact' ? link?.contact_id : link?.conversation_display_id
  ),
  type,
});
const linkRemovalContextMatches = context => {
  if (!context?.accountId || !context.cardId || !context.linkId) return false;
  const collection =
    context.type === 'contact'
      ? props.item?.additional_contacts || []
      : props.item?.additional_conversations || [];
  const matchingLink = collection.find(
    link => normalizePositiveId(link?.id) === context.linkId
  );
  if (!matchingLink) return false;
  const currentContext = linkRemovalContext(context.type, matchingLink);
  return Object.keys(context).every(
    key => currentContext[key] === context[key]
  );
};

const handleRemoveContact = async link => {
  if (removingContactId.value) return;
  const removalContext = linkRemovalContext('contact', link);
  if (!linkRemovalContextMatches(removalContext)) return;
  const confirmed = await requestConfirmation({
    title: t('PIPELINE.PANELS.LINKS.REMOVE_CONTACT_TITLE', {
      name: link.name || '',
    }),
    description: t('PIPELINE.PANELS.LINKS.REMOVE_CONTACT_CONFIRM', {
      name: link.name || '',
    }),
    label: t('PIPELINE.PANELS.LINKS.REMOVE_ACTION'),
  });
  if (
    !confirmed ||
    removingContactId.value ||
    !linkRemovalContextMatches(removalContext)
  ) {
    return;
  }
  removingContactId.value = removalContext.linkId;
  try {
    await PipelineCardLinksAPI.removeContact(
      removalContext.cardId,
      removalContext.linkId,
      { accountId: removalContext.accountId }
    );
    if (!linkRemovalContextMatches(removalContext)) return;
    showAlert(t('PIPELINE.PANELS.LINKS.REMOVE_CONTACT_SUCCESS'));
    emit('itemUpdated');
  } catch {
    if (linkRemovalContextMatches(removalContext)) {
      showAlert(t('PIPELINE.PANELS.LINKS.REMOVE_CONTACT_ERROR'));
    }
  } finally {
    if (removingContactId.value === removalContext.linkId) {
      removingContactId.value = null;
    }
  }
};

// ── Time in stage ─────────────────────────────────────────────────────────────

const timeInStage = computed(() => {
  const dateStr = props.item?.stage_entered_at || props.item?.created_at;
  if (!dateStr) return '0m';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return `${Math.floor(diffHours / 24)}d`;
});

// ── Checklist progress ────────────────────────────────────────────────────────

const checklistPercent = computed(() => {
  if (!props.checklistTotal) return 0;
  return Math.round((props.checklistCompleted / props.checklistTotal) * 100);
});

const inlineEditLocale = computed(() =>
  String(locale?.value || locale || 'en-US').replace(/_/g, '-')
);

const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const reportingTimezone = computed(() => resolveReportingTimezone(store));

const inlineEditCurrencyCode = computed(() => {
  const account = currentAccount.value;
  const currency =
    props.item?.currency ||
    props.item?.item_details?.currency ||
    account.currency ||
    account.settings?.currency ||
    'BRL';
  const code =
    typeof currency === 'object'
      ? currency.code || currency.iso_code || currency.value
      : currency;
  return /^[A-Z]{3}$/i.test(String(code || ''))
    ? String(code).toUpperCase()
    : 'BRL';
});

// ── Item info — field values ──────────────────────────────────────────────────

// `item_details.value` is the manually edited card value. The revenue column is
// a fallback because forecast recalculation may update it independently.
const itemValue = computed(() => {
  return getEffectiveCardValue(props.item) ?? '';
});
const normalizeItemValue = value => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return { valid: true, value: null };
  }

  const numericValue =
    typeof value === 'number' ? value : Number(String(value).trim());
  return {
    valid: Number.isFinite(numericValue) && numericValue >= 0,
    value: numericValue,
  };
};
const itemValueIsValid = value =>
  normalizeItemValue(value).valid ? true : t('PIPELINE.ERRORS.VALUE_INVALID');
const saveItemValue = value => {
  const normalizedValue = normalizeItemValue(value);
  if (normalizedValue.valid) {
    emit('updateField', 'value', normalizedValue.value);
  }
};
const deadlineAt = computed(() => {
  const value = props.item?.deadline ?? props.item?.item_details?.deadline_at;
  return value
    ? formatInReportingTimezone(
        value,
        "yyyy-MM-dd'T'HH:mm",
        reportingTimezone.value
      )
    : '';
});
const deadlineInputIsValid = value => {
  if (!value) return true;
  const [date, time] = String(value).split('T');
  return zonedDateTimeToUtc(date, time, reportingTimezone.value)
    ? true
    : t('PIPELINE.ERRORS.SCHEDULE_INVALID');
};
const saveDeadline = value => {
  if (!value) {
    emit('updateField', 'deadline', null);
    return;
  }

  const [date, time] = String(value).split('T');
  const utcDeadline = zonedDateTimeToUtc(date, time, reportingTimezone.value);
  if (utcDeadline) {
    emit('updateField', 'deadline', utcDeadline.toISOString());
  }
};
const forecastCloseDate = computed(
  () =>
    props.item?.forecast_close_date ??
    props.item?.item_details?.forecast_close_date ??
    ''
);
const leadScore = computed(
  () => props.item?.lead_score ?? props.item?.item_details?.lead_score ?? null
);

// ── Linked conversation ───────────────────────────────────────────────────────

const conversationData = computed(
  () => props.item?.item_details?.conversation || null
);

const hasLinkedConversation = computed(
  () => !!(conversationData.value || props.item?.item_details?.conversation_id)
);

const conversationDisplayId = computed(
  () =>
    conversationData.value?.display_id ||
    props.item?.conversation_display_id ||
    null
);

const currentUnlinkContext = () => ({
  accountId: normalizePositiveId(store.getters.getCurrentAccountId),
  cardId: normalizePositiveId(props.item?.id),
  contactId: normalizePositiveId(contactId.value),
  conversationDisplayId: normalizePositiveId(conversationDisplayId.value),
  conversationId: normalizePositiveId(
    props.item?.item_details?.conversation_id || conversationData.value?.id
  ),
});
const unlinkContextMatches = context => {
  const currentContext = currentUnlinkContext();
  return Object.keys(currentContext).every(
    key => currentContext[key] === context[key]
  );
};
let unlinkContextVersion = 0;
watch(
  () => Object.values(currentUnlinkContext()),
  () => {
    unlinkContextVersion += 1;
  }
);

const conversationStatus = computed(
  () => conversationData.value?.status || null
);

const lastMessagePreview = computed(
  () => props.item?.item_details?.conversation_last_message_preview || null
);

const statusBadgeClass = computed(() => {
  const s = conversationStatus.value;
  if (s === 'open') return 'bg-emerald-3 text-emerald-11';
  if (s === 'resolved') return 'bg-n-alpha-1 text-n-slate-11';
  if (s === 'pending') return 'bg-amber-3 text-amber-11';
  return 'bg-n-alpha-1 text-n-slate-11';
});

const navigateToConversation = () => {
  if (!conversationDisplayId.value) return;
  const accountId = store.getters.getCurrentAccountId;
  try {
    router.push({
      name: 'inbox_conversation',
      params: { accountId, conversation_id: conversationDisplayId.value },
    });
  } catch {
    window.location.href = `/app/accounts/${accountId}/conversations/${conversationDisplayId.value}`;
  }
};

const handleUnlinkConversation = async () => {
  const unlinkContext = currentUnlinkContext();
  if (
    !unlinkContext.accountId ||
    !unlinkContext.cardId ||
    (!unlinkContext.conversationDisplayId && !unlinkContext.conversationId)
  ) {
    return;
  }
  const confirmationContextVersion = unlinkContextVersion;
  const confirmed = await requestConfirmation({
    title: t('PIPELINE.PANELS.UNLINK_CONVERSATION'),
    description: t('PIPELINE.PANELS.UNLINK_CONFIRM'),
    label: t('PIPELINE.PANELS.UNLINK_CONVERSATION'),
  });
  if (
    confirmed &&
    confirmationContextVersion === unlinkContextVersion &&
    unlinkContextMatches(unlinkContext)
  ) {
    emit('unlinkConversation', unlinkContext);
  }
};

// ── FR2: Additional conversations (multiple conversations per card) ──────────
// Backend: PipelineCard#as_json['additional_conversations'] — the primary
// conversation (conversation_display_id) is never duplicated (deduped
// server-side). MVP picker: type the conversation # directly (no existing
// cross-contact conversation search component to reuse for this scope).

const additionalConversations = computed(
  () => props.item?.additional_conversations || []
);

const showConversationInput = ref(false);
const conversationLinkInput = ref('');
const addingConversation = ref(false);
const removingConversationId = ref(null);

const toggleConversationInput = () => {
  showConversationInput.value = !showConversationInput.value;
  if (!showConversationInput.value) conversationLinkInput.value = '';
};

const goToConversation = displayId => {
  if (!displayId) return;
  const accountId = store.getters.getCurrentAccountId;
  try {
    router.push({
      name: 'inbox_conversation',
      params: { accountId, conversation_id: displayId },
    });
  } catch {
    window.location.href = `/app/accounts/${accountId}/conversations/${displayId}`;
  }
};

const handleAddConversation = async () => {
  const displayId = conversationLinkInput.value.trim();
  if (!displayId) return;
  addingConversation.value = true;
  try {
    await PipelineCardLinksAPI.addConversation(props.item.id, displayId);
    showAlert(t('PIPELINE.PANELS.LINKS.ADD_CONVERSATION_SUCCESS'));
    conversationLinkInput.value = '';
    showConversationInput.value = false;
    emit('itemUpdated');
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      error?.response?.data?.error ||
      t('PIPELINE.PANELS.LINKS.ADD_CONVERSATION_ERROR');
    showAlert(message);
  } finally {
    addingConversation.value = false;
  }
};

const handleRemoveAdditionalConversation = async link => {
  if (removingConversationId.value) return;
  const removalContext = linkRemovalContext('conversation', link);
  if (!linkRemovalContextMatches(removalContext)) return;
  const confirmed = await requestConfirmation({
    title: t('PIPELINE.PANELS.LINKS.REMOVE_CONVERSATION_TITLE', {
      id: link.conversation_display_id,
    }),
    description: t('PIPELINE.PANELS.LINKS.REMOVE_CONVERSATION_CONFIRM', {
      id: link.conversation_display_id,
    }),
    label: t('PIPELINE.PANELS.LINKS.REMOVE_ACTION'),
  });
  if (
    !confirmed ||
    removingConversationId.value ||
    !linkRemovalContextMatches(removalContext)
  ) {
    return;
  }
  removingConversationId.value = removalContext.linkId;
  try {
    await PipelineCardLinksAPI.removeConversation(
      removalContext.cardId,
      removalContext.linkId,
      { accountId: removalContext.accountId }
    );
    if (!linkRemovalContextMatches(removalContext)) return;
    showAlert(t('PIPELINE.PANELS.LINKS.REMOVE_CONVERSATION_SUCCESS'));
    emit('itemUpdated');
  } catch {
    if (linkRemovalContextMatches(removalContext)) {
      showAlert(t('PIPELINE.PANELS.LINKS.REMOVE_CONVERSATION_ERROR'));
    }
  } finally {
    if (removingConversationId.value === removalContext.linkId) {
      removingConversationId.value = null;
    }
  }
};

// ── Custom attributes ─────────────────────────────────────────────────────────

// Read from the same root `custom_attributes` JSONB column that
// `handlePanelFieldUpdate` (PipelineCardDetails.vue) writes to. Reading from
// `item_details.custom_attributes` here was a bug: writes landed on the root
// column, so saved values disappeared on the next refetch.
const customAttrValues = computed(() => props.item?.custom_attributes || {});

// Contact custom attributes (read-only — set on the contact record)
const contactCustomAttrs = computed(
  () =>
    props.item?.contact?.custom_attributes ||
    props.item?.item_details?.conversation?.contact?.custom_attributes ||
    {}
);

const contactAttrEntries = computed(() =>
  props.contactCustomAttributes
    .map(attr => ({
      key: attr.attribute_key,
      label: attr.attribute_display_name || attr.attribute_key,
      value: contactCustomAttrs.value[attr.attribute_key],
    }))
    .filter(
      entry =>
        entry.value !== undefined && entry.value !== null && entry.value !== ''
    )
);

const getAttrValue = key => customAttrValues.value[key] ?? '';

const handleCustomAttrSave = (key, newValue) => {
  emit('updateField', `custom_attributes.${key}`, newValue);
};

const attrInputType = attr => {
  const displayType =
    attr?.attribute_display_type || attr?.attribute_key_type || 'text';
  if (displayType === 'number') return 'number';
  if (displayType === 'date') return 'date';
  if (displayType === 'currency') return 'currency';
  if (displayType === 'list') return 'list';
  if (displayType === 'checkbox' || displayType === 'boolean')
    return 'checkbox';
  return 'text';
};

// Options for the `list` type — the dropdown values configured on the
// CustomAttributeDefinition (attribute_values).
const attrOptions = attr => attr?.attribute_values || [];

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(() => {
  panelOpen.value = loadPanelState();
});
</script>

<template>
  <div class="flex flex-col gap-3 p-4">
    <!-- ── Block 1: Contacts (FR2: primary + additional) ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.contact"
        :aria-controls="panelContentId('contact')"
        @click="togglePanel('contact')"
      >
        <span class="flex items-center gap-1.5">
          {{ t('PIPELINE.PANELS.CONTACT') }}
          <span
            v-if="additionalContacts.length"
            class="text-xs font-normal text-n-slate-10"
          >
            {{
              t('PIPELINE.PANELS.LINKS.COUNT', {
                count: additionalContacts.length + (contactId ? 1 : 0),
              })
            }}
          </span>
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.contact }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.contact"
        :id="panelContentId('contact')"
        class="px-4 py-3 border-t border-n-weak flex flex-col gap-3"
      >
        <!-- Primary contact -->
        <div v-if="contactId || contactName" class="flex items-center gap-3">
          <Avatar
            :src="contactAvatarUrl"
            :name="contactName || t('PIPELINE.PANELS.CONTACT')"
            :size="48"
            rounded-full
            class="flex-shrink-0"
          />
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1.5">
              <button
                v-if="contactId"
                type="button"
                class="min-h-11 text-sm font-medium text-n-brand hover:underline text-left truncate"
                @click="navigateToContact"
              >
                {{ contactName || '—' }}
              </button>
              <span v-else class="text-sm font-medium text-n-slate-12 truncate">
                {{ contactName || '—' }}
              </span>
              <span
                class="flex-shrink-0 text-[10px] font-medium uppercase tracking-wide text-n-brand bg-n-brand/10 px-1.5 py-0.5 rounded"
              >
                {{ t('PIPELINE.PANELS.LINKS.PRIMARY_BADGE') }}
              </span>
            </div>
            <div
              v-if="contactPhone"
              class="text-xs text-n-slate-11 mt-0.5 truncate"
            >
              {{ contactPhone }}
            </div>
            <div
              v-if="contactEmail"
              class="text-xs text-n-slate-11 mt-0.5 truncate"
            >
              {{ contactEmail }}
            </div>
          </div>
        </div>

        <!-- Additional contacts -->
        <div
          v-for="link in additionalContacts"
          :key="link.id"
          class="flex items-center gap-3"
        >
          <Avatar
            :src="link.avatar_url"
            :name="link.name || t('PIPELINE.PANELS.CONTACT')"
            :size="36"
            rounded-full
            class="flex-shrink-0"
          />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ link.name || '—' }}
            </p>
            <div
              v-if="link.phone_number"
              class="text-xs text-n-slate-11 mt-0.5 truncate"
            >
              {{ link.phone_number }}
            </div>
            <div
              v-else-if="link.email"
              class="text-xs text-n-slate-11 mt-0.5 truncate"
            >
              {{ link.email }}
            </div>
          </div>
          <button
            type="button"
            class="flex size-11 flex-shrink-0 items-center justify-center rounded text-n-slate-9 transition-colors duration-75 hover:bg-n-alpha-1 hover:text-n-ruby-9 disabled:opacity-50"
            :disabled="removingContactId === link.id"
            :aria-label="
              t('PIPELINE.PANELS.LINKS.REMOVE_CONTACT_TITLE', {
                name: link.name,
              })
            "
            :title="
              t('PIPELINE.PANELS.LINKS.REMOVE_CONTACT_TITLE', {
                name: link.name,
              })
            "
            @click="handleRemoveContact(link)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Inline contact picker -->
        <div
          v-if="showContactPicker"
          :id="contactPickerId"
          class="flex flex-col gap-1.5"
        >
          <label :for="contactSearchId" class="sr-only">
            {{ t('PIPELINE.PANELS.LINKS.SEARCH_CONTACT_LABEL') }}
          </label>
          <input
            :id="contactSearchId"
            v-model="contactQuery"
            name="pipeline-card-contact-search"
            type="text"
            class="!mb-0 min-h-11 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
            :placeholder="t('PIPELINE.PANELS.LINKS.SEARCH_CONTACT_PLACEHOLDER')"
            @input="searchContactsForAdd"
          />
          <div
            v-if="contactQuery.trim().length >= 2"
            class="border border-n-weak rounded-lg max-h-48 overflow-y-auto bg-n-solid-1"
          >
            <div
              v-if="contactSearchLoading"
              class="px-3 py-2 text-xs text-n-slate-10"
            >
              {{ t('PIPELINE.LOADING') }}
            </div>
            <template v-else-if="contactSearchResults.length">
              <button
                v-for="c in contactSearchResults"
                :key="c.id"
                type="button"
                class="flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-n-alpha-1 disabled:opacity-50"
                :disabled="addingContact"
                @click="handleAddContact(c)"
              >
                <Avatar
                  :src="c.thumbnail"
                  :name="c.name || t('PIPELINE.PANELS.CONTACT')"
                  :size="24"
                  rounded-full
                  class="flex-shrink-0"
                />
                <span class="truncate text-n-slate-12">
                  {{ c.name }}
                  <span v-if="c.email" class="text-n-slate-10">
                    <span aria-hidden="true">{{ metadataSeparator }}</span>
                    {{ c.email }}
                  </span>
                </span>
              </button>
            </template>
            <p v-else class="px-3 py-2 text-xs text-n-slate-10">
              {{ t('PIPELINE.PANELS.LINKS.SEARCH_CONTACT_EMPTY') }}
            </p>
          </div>
        </div>

        <!-- Add contact toggle -->
        <button
          type="button"
          class="flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-n-weak py-2 text-sm font-medium text-n-brand transition-colors hover:bg-n-alpha-1"
          :aria-expanded="showContactPicker"
          :aria-controls="contactPickerId"
          @click="toggleContactPicker"
        >
          {{
            showContactPicker
              ? t('PIPELINE.PANELS.LINKS.CANCEL')
              : t('PIPELINE.PANELS.LINKS.ADD_CONTACT_BTN')
          }}
        </button>
      </div>
    </div>

    <!-- ── Block 2: Statistics ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.statistics"
        :aria-controls="panelContentId('statistics')"
        @click="togglePanel('statistics')"
      >
        <span>{{ t('PIPELINE.PANELS.STATISTICS') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.statistics }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.statistics"
        :id="panelContentId('statistics')"
        class="px-4 py-3 border-t border-n-weak space-y-1"
      >
        <!-- Time in Stage -->
        <div class="flex items-center justify-between py-1.5 text-sm">
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.TIME_IN_STAGE')
          }}</span>
          <span class="font-medium text-n-slate-12">{{ timeInStage }}</span>
        </div>

        <!-- Notes -->
        <button
          type="button"
          class="-mx-1 flex min-h-11 w-full items-center justify-between rounded px-1 py-1.5 text-sm transition-colors hover:bg-n-alpha-1"
          @click="emit('goToTab', 'notes')"
        >
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.NOTES_COUNT')
          }}</span>
          <span class="font-medium text-n-slate-12">{{ notesCount }}</span>
        </button>

        <!-- Checklist -->
        <button
          type="button"
          class="-mx-1 flex min-h-11 w-full items-center justify-between rounded px-1 py-1.5 text-sm transition-colors hover:bg-n-alpha-1"
          @click="emit('goToTab', 'checklist')"
        >
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.CHECKLIST_COUNT')
          }}</span>
          <div class="flex items-center gap-2">
            <span class="font-medium text-n-slate-12 text-xs">
              {{ `${checklistCompleted}/${checklistTotal}` }}
            </span>
            <ProgressBar
              :value="checklistPercent"
              size="sm"
              class="w-16"
              :label="t('PIPELINE.PANELS.CHECKLIST_COUNT')"
            />
          </div>
        </button>

        <!-- Attachments -->
        <button
          type="button"
          class="-mx-1 flex min-h-11 w-full items-center justify-between rounded px-1 py-1.5 text-sm transition-colors hover:bg-n-alpha-1"
          @click="emit('goToTab', 'attachments')"
        >
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.ATTACHMENTS_COUNT')
          }}</span>
          <span class="font-medium text-n-slate-12">{{
            attachmentsCount
          }}</span>
        </button>

        <!-- Stage Changes (non-clickable — history tab removed) -->
        <div
          class="flex items-center justify-between py-1.5 text-sm px-1 -mx-1"
        >
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.STAGE_CHANGES_COUNT')
          }}</span>
          <span class="font-medium text-n-slate-12">{{
            stageChangesCount
          }}</span>
        </div>
      </div>
    </div>

    <!-- ── Block 3: Item Information ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.itemInfo"
        :aria-controls="panelContentId('item-info')"
        @click="togglePanel('itemInfo')"
      >
        <span>{{ t('PIPELINE.PANELS.ITEM_INFO') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.itemInfo }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.itemInfo"
        :id="panelContentId('item-info')"
        class="px-4 py-3 border-t border-n-weak space-y-3"
      >
        <!-- Value -->
        <div>
          <p class="text-xs text-n-slate-10 mb-1">
            {{ t('PIPELINE.PANELS.VALUE') }}
          </p>
          <InlineEditField
            :model-value="itemValue"
            type="currency"
            :accessible-label="t('PIPELINE.PANELS.VALUE')"
            :currency-code="inlineEditCurrencyCode"
            :locale="inlineEditLocale"
            :validator="itemValueIsValid"
            empty-label="—"
            @save="saveItemValue"
          />
        </div>

        <!-- Deadline -->
        <div>
          <p class="text-xs text-n-slate-10 mb-1">
            {{ t('PIPELINE.PANELS.DEADLINE') }}
          </p>
          <InlineEditField
            :model-value="deadlineAt"
            type="datetime"
            :accessible-label="t('PIPELINE.PANELS.DEADLINE')"
            :locale="inlineEditLocale"
            :time-zone="reportingTimezone"
            :validator="deadlineInputIsValid"
            empty-label="—"
            @save="saveDeadline"
          />
        </div>

        <!-- Forecast Close Date -->
        <div>
          <p class="text-xs text-n-slate-10 mb-1">
            {{ t('PIPELINE.PANELS.FORECAST_CLOSE') }}
          </p>
          <InlineEditField
            :model-value="forecastCloseDate"
            type="date"
            :accessible-label="t('PIPELINE.PANELS.FORECAST_CLOSE')"
            :locale="inlineEditLocale"
            :time-zone="reportingTimezone"
            empty-label="—"
            @save="val => emit('updateField', 'forecast_close_date', val)"
          />
        </div>

        <!-- Lead Score -->
        <div>
          <div class="flex items-center justify-between">
            <p class="text-xs text-n-slate-10">
              {{ t('PIPELINE.PANELS.LEAD_SCORE') }}
            </p>
            <button
              type="button"
              class="min-h-11 rounded px-2 text-xs text-n-brand hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              @click="emit('overrideLeadScore')"
            >
              {{ t('PIPELINE.PANELS.OVERRIDE_LEAD_SCORE') }}
            </button>
          </div>
          <span class="text-sm font-medium text-n-slate-12">
            {{
              leadScore !== null
                ? leadScore
                : t('PIPELINE.INLINE_EDIT.EMPTY_VALUE')
            }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Block 4: Conversations (FR2: primary + additional) ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.linkedConversation"
        :aria-controls="panelContentId('linked-conversation')"
        @click="togglePanel('linkedConversation')"
      >
        <span class="flex items-center gap-1.5">
          {{ t('PIPELINE.PANELS.LINKED_CONVERSATION') }}
          <span
            v-if="additionalConversations.length"
            class="text-xs font-normal text-n-slate-10"
          >
            {{
              t('PIPELINE.PANELS.LINKS.COUNT', {
                count:
                  additionalConversations.length +
                  (hasLinkedConversation ? 1 : 0),
              })
            }}
          </span>
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.linkedConversation }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.linkedConversation"
        :id="panelContentId('linked-conversation')"
        class="px-4 py-3 border-t border-n-weak flex flex-col gap-3"
      >
        <!-- Primary — linked -->
        <div v-if="hasLinkedConversation">
          <div class="flex items-center justify-between gap-2">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ `#${conversationDisplayId}` }}
                <span
                  class="ml-1 text-[10px] font-medium uppercase tracking-wide text-n-brand bg-n-brand/10 px-1.5 py-0.5 rounded"
                >
                  {{ t('PIPELINE.PANELS.LINKS.PRIMARY_BADGE') }}
                </span>
                <span v-if="contactName" class="text-n-slate-11 font-normal">
                  {{ `— ${contactName}` }}
                </span>
              </p>
              <p
                v-if="lastMessagePreview"
                class="text-xs text-n-slate-10 truncate mt-0.5"
              >
                {{ lastMessagePreview }}
              </p>
              <span
                v-if="conversationStatus"
                class="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-xs font-medium"
                :class="statusBadgeClass"
              >
                {{ conversationStatus }}
              </span>
            </div>
            <div class="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                class="min-h-11 rounded border border-n-weak px-3 py-2 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-1"
                @click="navigateToConversation"
              >
                {{ t('PIPELINE.PANELS.VIEW_CONVERSATION') }}
              </button>
              <button
                type="button"
                class="min-h-11 rounded border border-n-ruby-6 px-3 py-2 text-xs font-medium text-n-ruby-11 transition-colors hover:bg-n-ruby-3"
                @click="handleUnlinkConversation"
              >
                {{ t('PIPELINE.PANELS.UNLINK_CONVERSATION') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Primary — not linked -->
        <div v-else>
          <button
            type="button"
            class="flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-n-weak py-2 text-sm font-medium text-n-brand transition-colors hover:bg-n-alpha-1"
            @click="emit('linkConversation')"
          >
            {{ t('PIPELINE.PANELS.LINK_CONVERSATION_BTN') }}
          </button>
        </div>

        <!-- Additional conversations -->
        <div
          v-for="link in additionalConversations"
          :key="link.id"
          class="flex items-center justify-between gap-2"
        >
          <button
            type="button"
            class="min-h-11 truncate text-left text-sm font-medium text-n-brand hover:underline"
            @click="goToConversation(link.conversation_display_id)"
          >
            {{ `#${link.conversation_display_id}` }}
          </button>
          <button
            type="button"
            class="flex size-11 flex-shrink-0 items-center justify-center rounded text-n-slate-9 transition-colors duration-75 hover:bg-n-alpha-1 hover:text-n-ruby-9 disabled:opacity-50"
            :disabled="removingConversationId === link.id"
            :aria-label="
              t('PIPELINE.PANELS.LINKS.REMOVE_CONVERSATION_TITLE', {
                id: link.conversation_display_id,
              })
            "
            :title="
              t('PIPELINE.PANELS.LINKS.REMOVE_CONVERSATION_TITLE', {
                id: link.conversation_display_id,
              })
            "
            @click="handleRemoveAdditionalConversation(link)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Inline add-by-display-id -->
        <div
          v-if="showConversationInput"
          :id="conversationInputId"
          class="flex items-center gap-2"
        >
          <label :for="conversationFieldId" class="sr-only">
            {{ t('PIPELINE.PANELS.LINKS.CONVERSATION_ID_LABEL') }}
          </label>
          <input
            :id="conversationFieldId"
            v-model="conversationLinkInput"
            name="pipeline-card-conversation-id"
            type="text"
            inputmode="numeric"
            class="!mb-0 min-h-11 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
            :placeholder="
              t('PIPELINE.PANELS.LINKS.CONVERSATION_ID_PLACEHOLDER')
            "
            @keyup.enter="handleAddConversation"
          />
          <button
            type="button"
            class="min-h-11 flex-shrink-0 rounded-lg bg-n-brand px-3 py-2 text-xs font-medium text-white hover:brightness-110 disabled:opacity-50"
            :disabled="addingConversation || !conversationLinkInput.trim()"
            @click="handleAddConversation"
          >
            {{ t('PIPELINE.PANELS.LINKS.ADD_CONVERSATION_SUBMIT') }}
          </button>
        </div>

        <!-- Add conversation toggle -->
        <button
          type="button"
          class="flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-n-weak py-2 text-sm font-medium text-n-brand transition-colors hover:bg-n-alpha-1"
          :aria-expanded="showConversationInput"
          :aria-controls="conversationInputId"
          @click="toggleConversationInput"
        >
          {{
            showConversationInput
              ? t('PIPELINE.PANELS.LINKS.CANCEL')
              : t('PIPELINE.PANELS.LINKS.ADD_CONVERSATION_BTN')
          }}
        </button>
      </div>
    </div>

    <!-- ── Block 5: Additional Data (custom attributes) ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.additionalData"
        :aria-controls="panelContentId('additional-data')"
        @click="togglePanel('additionalData')"
      >
        <span>{{ t('PIPELINE.PANELS.ADDITIONAL_DATA') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.additionalData }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.additionalData"
        :id="panelContentId('additional-data')"
        class="px-4 py-3 border-t border-n-weak space-y-3"
      >
        <template v-if="customAttributes.length > 0">
          <div
            v-for="attr in customAttributes"
            :key="attr.attribute_key || attr.id"
          >
            <p class="text-xs text-n-slate-10 mb-1">
              {{ attr.attribute_display_name || attr.attribute_key }}
            </p>
            <InlineEditField
              :model-value="getAttrValue(attr.attribute_key)"
              :type="attrInputType(attr)"
              :options="attrOptions(attr)"
              :accessible-label="
                attr.attribute_display_name || attr.attribute_key
              "
              :currency-code="inlineEditCurrencyCode"
              :locale="inlineEditLocale"
              empty-label="—"
              @save="val => handleCustomAttrSave(attr.attribute_key, val)"
            />
          </div>
        </template>

        <p v-else class="text-sm text-n-slate-10 italic">
          {{ t('PIPELINE.PANELS.EMPTY_CUSTOM_ATTRIBUTES') }}
        </p>
      </div>
    </div>

    <!-- ── Block 6: Custom Fields (contact custom attributes, read-only) ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.customFields"
        :aria-controls="panelContentId('custom-fields')"
        @click="togglePanel('customFields')"
      >
        <span>{{ t('PIPELINE.PANELS.CUSTOM_FIELDS') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.customFields }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.customFields"
        :id="panelContentId('custom-fields')"
        class="px-4 py-3 border-t border-n-weak space-y-3"
      >
        <template v-if="contactAttrEntries.length > 0">
          <div
            v-for="entry in contactAttrEntries"
            :key="entry.key"
            class="flex flex-col gap-1"
          >
            <p class="text-xs text-n-slate-10">{{ entry.label }}</p>
            <p class="text-sm text-n-slate-12 break-words">{{ entry.value }}</p>
          </div>
        </template>

        <p v-else class="text-sm text-n-slate-10 italic">
          {{ t('PIPELINE.PANELS.EMPTY_CUSTOM_FIELDS') }}
        </p>
      </div>
    </div>

    <ConfirmationModal
      ref="actionConfirmationRef"
      :title="confirmationTitle"
      :description="confirmationDescription"
      :confirm-label="confirmationLabel"
      :cancel-label="t('PIPELINE.CANCEL')"
    />
  </div>
</template>
